import DownloadCSVMenuButton, {
    downloadTypes,
} from '../Button/DownloadCSVMenuButton';
import { FiChevronDown, FiDownload, FiList, FiMapPin } from 'react-icons/fi';
import { tooltips, views } from '@/copy';
import {
    useCurrentCountry,
    useEndDate,
    useFetchCountries,
    useFetchUserAbout,
    useGoogleData,
    useSort,
    useUserIndustries,
} from 'utils/hooks';
import { useLayoutEffect, useRef, useState } from 'react';
import DefinitionTooltip from '../DefinitionTooltip';
import Drawer from '../Drawer';
import DrawerContent from '../DrawerContent';
import FilterCheckboxChip from '../FilterChips/FilterCheckboxChip';
import { GOOGLE_SORT_OPTIONS } from 'utils/metrics';
import { ParentSize } from '@visx/responsive';
import { PlaceholderShimmerBackground } from 'utils/fragments';
import SectionTable from './SectionTable';
import { Select } from '../Input';
import { SoftButton } from '../Button';
import Stack from '../Stack';
import { TIKTOK_COUNTRIES } from '@/config/constants';
import TableHeading from '../TableHeading';
import WorldMap from './WorldMap';
import downloadImage from 'utils/downloadImage';
import { buildExportFilename, downloadItemsAsCSV } from 'utils/formatCSV';
import isFinite from 'lodash/isFinite';
import sortDataByKey from 'utils/sortDataByKey';
import styled from 'styled-components';
import { withMixpanel } from 'utils/app';

const sortOptions = [
    {
        label: 'volume by category',
        value: `volume by category ${views.descending}`,
        sortBy: 'industryIndex',
        ascending: false,
        tooltip: tooltips.industryIndex,
        mixpanelContentKey: 'industryIndex',
    },
    {
        label: 'volume by population',
        value: `volume by population ${views.descending}`,
        sortBy: 'perCapitaIndex',
        ascending: false,
        tooltip: tooltips.perCapitaIndex,
        mixpanelContentKey: 'perCapitaIndex',
    },
    GOOGLE_SORT_OPTIONS.YoY,
];

function WithTooltip({ children, countries, tooltip, mixpanelContentKey }) {
    if (!(tooltip && mixpanelContentKey)) return children;
    return (
        <DefinitionTooltip
            {...tooltip}
            countries={countries}
            mixpanelMeta={{ contentKey: mixpanelContentKey }}
        >
            <div>{children}</div>
        </DefinitionTooltip>
    );
}

const MapContainer = styled.div.attrs(({ shimmerWidth = '10000px' }) => ({
    shimmerWidth,
}))`
    height: 40vw;
    max-height: 25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius_md};
    background-color: ${({ theme, isLoading, isEmpty }) =>
        isLoading || isEmpty ? theme.gray_5 : 'transparent'};
    ${({ isLoading }) => (isLoading ? PlaceholderShimmerBackground : null)}
`;

function GeoTable({ categoryId, name, ...props }) {
    const countriesQuery = useFetchCountries();
    const country = useCurrentCountry();
    const date = useEndDate('google');
    const countryIds = countriesQuery.data
        ? countriesQuery.data.map(({ id }) => id)
        : null;

    return (
        <SectionTable
            title={views.categoryThree.sections.markets.title}
            subtitle={views.categoryThree.sections.markets.subtitle}
            tooltipKey="marketsTitleHelp"
            paginateBy="countries"
            countries={countryIds}
            categories={categoryId ? [categoryId] : null}
            name={name}
            source="google"
            filename={buildExportFilename({
                date,
                country,
                source: 'google',
                entity: name,
                type: 'market',
                kind: 'data',
            })}
            {...props}
        />
    );
}

const MarketsMapSection = withMixpanel(
    ({ name, categoryId, hierarchy_hint, controller, mixpanel, tools }) => {
        const [details, setDetails] = useState(null);
        const country = useCurrentCountry();
        const date = useEndDate('google');
        const aboutQuery = useFetchUserAbout();
        const countriesQuery = useFetchCountries();
        const countryIds = countriesQuery.data
            ? countriesQuery.data.map(({ id }) => id)
            : null;
        const marketsQuery = useGoogleData({
            enabled: !!(categoryId && countryIds),
            data: {
                categories: [categoryId],
                countries: countryIds,
            },
            paginateBy: 'countries',
            minimal: true,
            controller,
        });
        const categoriesQuery = useGoogleData({
            enabled: !!(categoryId && countryIds),
            data: {
                categories: [categoryId],
                countries: countryIds ? countryIds.slice(0, 1) : null,
            },
            paginateBy: 'categories',
            minimal: true,
            controller,
        });
        const industriesQuery = useUserIndustries();
        const currentCategoryDepth =
            categoriesQuery.data &&
            categoriesQuery.data[0] &&
            categoriesQuery.data[0].depth
                ? categoriesQuery.data[0].depth
                : null;
        const currentIndustry =
            industriesQuery.data && hierarchy_hint
                ? industriesQuery.data.find(
                      ({ name }) => name === hierarchy_hint.cat1,
                  )
                : null;
        const cat2sQuery = useGoogleData({
            enabled: !!(
                currentIndustry &&
                currentIndustry.children &&
                countryIds
            ),
            data: {
                categories: currentIndustry ? currentIndustry.children : null,
                countries: countryIds ? countryIds.slice(0, 1) : null,
            },
            paginateBy: 'categories',
            minimal: true,
            controller,
        });
        const currentCat2 =
            cat2sQuery.data && hierarchy_hint
                ? cat2sQuery.data.find(
                      ({ name }) => name === hierarchy_hint.cat2,
                  )
                : null;
        const indexId = currentCat2 ? currentCat2.id : null;
        const currentIndustryMarketsQuery = useGoogleData({
            enabled: !!(indexId && countryIds),
            data: {
                categories: [indexId],
                countries: countryIds,
            },
            paginateBy: 'countries',
            minimal: true,
            controller,
        });

        const industryIndexes = [];
        const perCapitaIndexes = [];

        let modifiedMarketsQuery =
            marketsQuery.data && currentIndustryMarketsQuery.data
                ? marketsQuery.data.map((dataPoint) => {
                      let industryIndex = null;
                      let perCapitaIndex = null;
                      const rootVolume = (
                          currentIndustryMarketsQuery.data.find(
                              ({ code }) => dataPoint.code === code,
                          ) || {}
                      ).volume;
                      const internetUsingPopulation =
                          dataPoint.internet_using_population;

                      if (
                          isFinite(dataPoint.volume) &&
                          isFinite(rootVolume) &&
                          isFinite(dataPoint.volume / rootVolume) &&
                          currentCategoryDepth &&
                          currentCategoryDepth !== 'cat2'
                      ) {
                          industryIndex = dataPoint.volume / rootVolume;
                          industryIndexes.push(industryIndex);
                      }

                      if (
                          isFinite(dataPoint.volume) &&
                          isFinite(internetUsingPopulation)
                      ) {
                          perCapitaIndex =
                              dataPoint.volume / internetUsingPopulation;
                          perCapitaIndexes.push(perCapitaIndex);
                      }

                      return { ...dataPoint, perCapitaIndex, industryIndex };
                  })
                : null;

        const maxIndustryIndex = Math.max(...industryIndexes);
        const maxPerCapitaIndexes = Math.max(...perCapitaIndexes);

        modifiedMarketsQuery = modifiedMarketsQuery
            ? modifiedMarketsQuery.map((dataPoint) => {
                  return {
                      ...dataPoint,
                      perCapitaIndex: isFinite(dataPoint.perCapitaIndex)
                          ? (dataPoint.perCapitaIndex / maxPerCapitaIndexes) *
                            100
                          : null,
                      industryIndex: isFinite(dataPoint.industryIndex)
                          ? (dataPoint.industryIndex / maxIndustryIndex) * 100
                          : null,
                  };
              })
            : null;

        const mixpanelMeta = {
            contentKey: 'GeoSection',
            country: country ? country.name : null,
        };

        const trackMetricChange = (nextMetric) =>
            mixpanel.track('Change', {
                context: 'FilterCheckboxChip',
                ...mixpanelMeta,
                ...nextMetric,
            });

        const trackMarketClick = (nextMarket) =>
            mixpanel.track('Click', {
                context: 'MapBubble',
                ...mixpanelMeta,
                code: nextMarket.code,
                name: nextMarket.name,
            });

        const { setSortOption, sortOption, sortedData } = useSort(
            modifiedMarketsQuery,
            sortDataByKey,
            sortOptions[0],
        );

        useLayoutEffect(() => {
            if (
                currentCategoryDepth === 'cat2' &&
                sortOption.sortBy === 'industryIndex'
            ) {
                setSortOption(sortOptions[1]);
            }
        });

        const imageRef = useRef();

        const isLoading = [
            aboutQuery,
            countriesQuery,
            marketsQuery,
            categoriesQuery,
            industriesQuery,
            currentIndustryMarketsQuery,
        ].some(
            (query) =>
                query.isLoading ||
                (Array.isArray(query.data) &&
                    query.data.some(
                        ({ fetching, fetchingData }) =>
                            fetching || fetchingData,
                    )),
        );

        const onMarketClick = (market) => {
            setDetails({
                paginateBy: `countryId`,
                categoryId,
                countryId: market.id,
                skipPrediction: TIKTOK_COUNTRIES.includes(market.text_id),
            });
            trackMarketClick(market);
        };

        let fallback = null;
        if (marketsQuery.isError) {
            fallback = views.categoryThree.sections.markets.error;
        } else if (!sortedData || sortedData.length < 2) {
            fallback = views.categoryThree.sections.markets.noData;
        }

        return (
            <>
                <div>
                    <TableHeading
                        icon="geo"
                        title={views.categoryThree.sections.markets.title}
                        subtitle={views.categoryThree.sections.markets.subtitle}
                        tooltipKey="marketsTitleHelp"
                        tools={
                            <>
                                {tools}
                                <DownloadCSVMenuButton
                                    isDisabled={
                                        !(
                                            !(fallback || isLoading) &&
                                            sortedData &&
                                            sortedData.length
                                        )
                                    }
                                    isLoading={isLoading}
                                    options={[
                                        {
                                            mixpanelMeta: {
                                                label: 'global csv',
                                            },
                                            children: 'Countries CSV',
                                            onClick: () =>
                                                downloadItemsAsCSV(
                                                    buildExportFilename({
                                                        date,
                                                        country,
                                                        source: 'google',
                                                        entity: name,
                                                        type: 'market',
                                                        kind: 'data',
                                                    }),
                                                    sortedData,
                                                    [{ key: 'name' }].concat(
                                                        sortOptions.map(
                                                            ({
                                                                sortBy,
                                                                label,
                                                            }) => {
                                                                const column = {
                                                                    key: sortBy,
                                                                };
                                                                if (
                                                                    sortBy !==
                                                                    label
                                                                )
                                                                    column.label =
                                                                        label;
                                                                return column;
                                                            },
                                                        ),
                                                    ),
                                                ),
                                            downloadType: downloadTypes.CSV,
                                            key: 'global csv',
                                        },
                                        {
                                            mixpanelMeta: {
                                                label: 'global image',
                                            },
                                            children: 'Countries Image',
                                            onClick: () =>
                                                downloadImage(
                                                    imageRef.current,
                                                    {
                                                        title: `${name} ${sortOption.label}`,
                                                        filename:
                                                            buildExportFilename(
                                                                {
                                                                    date,
                                                                    country,
                                                                    source: 'google',
                                                                    entity: name,
                                                                    type: 'market',
                                                                    kind: 'data',
                                                                },
                                                            ),
                                                    },
                                                ),
                                            downloadType: downloadTypes.IMAGE,
                                            key: 'global image',
                                        },
                                    ]}
                                    mixpanelMeta={mixpanelMeta}
                                    buttonProps={{
                                        as: SoftButton,
                                        IconLeft: FiDownload,
                                        IconRight: FiChevronDown,
                                        children: 'Export Data',
                                    }}
                                />
                            </>
                        }
                        count={
                            !isLoading && sortedData ? sortedData.length : null
                        }
                    />
                    <Stack
                        $space={(theme) => `${theme.space_9}px;`}
                        css={`
                            padding: ${({ theme }) => theme.space_9}px;
                        `}
                    >
                        <Stack
                            $direction="row"
                            $space=".75rem"
                            css="display: flex;"
                        >
                            {sortOptions.map((currentSortOption) => {
                                if (
                                    currentCategoryDepth &&
                                    currentCategoryDepth === 'cat2' &&
                                    currentSortOption.sortBy === 'industryIndex'
                                ) {
                                    return (
                                        <FilterCheckboxChip
                                            checked={false}
                                            disabled={true}
                                            key={currentSortOption.value}
                                        >
                                            <span>
                                                {currentSortOption.label}
                                            </span>
                                        </FilterCheckboxChip>
                                    );
                                }

                                return (
                                    <WithTooltip
                                        countries={
                                            countriesQuery.data
                                                ? countriesQuery.data
                                                      .map(({ code }) => code)
                                                      .join(', ')
                                                : null
                                        }
                                        sortBy={currentSortOption.sortBy}
                                        tooltip={currentSortOption.tooltip}
                                        mixpanelContentKey={
                                            currentSortOption.mixpanelContentKey
                                        }
                                        key={currentSortOption.value}
                                    >
                                        <FilterCheckboxChip
                                            checked={
                                                sortOption.value ===
                                                currentSortOption.value
                                            }
                                            onChange={() => {
                                                setSortOption(
                                                    currentSortOption,
                                                );
                                                trackMetricChange(
                                                    currentSortOption,
                                                );
                                            }}
                                        >
                                            <span>
                                                {currentSortOption.label}
                                            </span>
                                        </FilterCheckboxChip>
                                    </WithTooltip>
                                );
                            })}
                        </Stack>
                        <div ref={imageRef}>
                            <MapContainer
                                isLoading={isLoading}
                                isEmpty={!!fallback}
                            >
                                {isLoading || fallback ? (
                                    fallback
                                ) : (
                                    <ParentSize>
                                        {({ width, height }) => {
                                            return (
                                                <WorldMap
                                                    width={width}
                                                    height={height}
                                                    data={sortedData}
                                                    sortOption={sortOption}
                                                    onMarketClick={
                                                        onMarketClick
                                                    }
                                                />
                                            );
                                        }}
                                    </ParentSize>
                                )}
                            </MapContainer>
                        </div>
                    </Stack>
                </div>
                <Drawer open={!!details} onClose={() => setDetails(null)}>
                    {details && (
                        <DrawerContent
                            showAddToWatchlist={false}
                            source="google"
                            {...details}
                        />
                    )}
                </Drawer>
            </>
        );
    },
);

const visualizationTypes = {
    MAP: 'MAP',
    TABLE: 'TABLE',
};

const visualizationOptions = [
    {
        label: 'Map',
        value: visualizationTypes.MAP,
        IconLeft: FiMapPin,
    },
    {
        label: 'Table',
        value: visualizationTypes.TABLE,
        IconLeft: FiList,
    },
];

const VertLine = styled.span`
    border-left: solid 1px ${({ theme }) => theme.gray_5};
    min-height: 16px;
    max-height: 16px;
    margin: 0px ${({ theme }) => theme.space_3}px;
`;

function MarketsSection(props) {
    const [visualization, setVisualization] = useState(visualizationOptions[0]);
    const tools = (
        <>
            <Select
                onChange={(selected) => setVisualization(selected)}
                value={visualization}
                options={visualizationOptions}
                $menuAlign="left"
                buttonProps={{
                    as: SoftButton,
                    selectLabel: 'View As',
                    size: 'sm',
                }}
                mixpanelContext="VisualizationSelect"
                mixpanelMeta={{ contentKey: 'GeoSection' }}
                handleMixpanelEvent={({ value }) => ({ value })}
            />
            <VertLine />
        </>
    );
    switch (visualization.value) {
        case visualizationTypes.MAP:
            return <MarketsMapSection {...props} tools={tools} />;
        case visualizationTypes.TABLE:
            return <GeoTable {...props} tools={tools} />;
        default:
            throw new Error(
                `Unable to render visualization "${visualization.value}"`,
            );
    }
}

export default MarketsSection;
